/** Local-clock preferences shared by the native scheduler and its editor. */
export type LearningSchedule = {
  enabled: boolean;
  start: string;
  end: string;
  jobIds: string[] | 'all';
};
export type ScheduledLearningRun = {
  jobId: string;
  jobName: string;
  startedAt: number;
  stopsAt: number;
};
export type LearningScheduleState = {
  schedule: LearningSchedule | null;
  run: ScheduledLearningRun | null;
  hold: string | null;
};
export const defaultLearningSchedule: LearningSchedule = {
  enabled: false,
  start: '21:00',
  end: '05:00',
  jobIds: [],
};
export const parseTimeOfDay = (value: string): number | null => {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value);
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};
export function validateLearningSchedule(value: unknown): LearningSchedule {
  const input = value as Partial<LearningSchedule> | null;
  if (
    !input ||
    typeof input.enabled !== 'boolean' ||
    typeof input.start !== 'string' ||
    typeof input.end !== 'string' ||
    parseTimeOfDay(input.start) === null ||
    parseTimeOfDay(input.end) === null ||
    !(
      input.jobIds === 'all' ||
      (Array.isArray(input.jobIds) &&
        input.jobIds.length <= 10_000 &&
        input.jobIds.every((id) => typeof id === 'string' && id.length > 0 && id.length <= 256))
    )
  )
    throw new Error('Choose valid learning times and jobs.');
  return {
    enabled: input.enabled,
    start: input.start,
    end: input.end,
    jobIds: input.jobIds === 'all' ? 'all' : [...new Set(input.jobIds)],
  };
}
export function insideLearningWindow(
  schedule: Pick<LearningSchedule, 'start' | 'end'>,
  now: Date,
): boolean {
  const start = parseTimeOfDay(schedule.start),
    end = parseTimeOfDay(schedule.end);
  if (start === null || end === null || start === end) return false;
  const minute = now.getHours() * 60 + now.getMinutes();
  return start < end ? minute >= start && minute < end : minute >= start || minute < end;
}
export function learningWindowEndsAt(schedule: Pick<LearningSchedule, 'end'>, now: number): number {
  const end = parseTimeOfDay(schedule.end);
  if (end === null) return now;
  const boundary = new Date(now);
  boundary.setHours(Math.floor(end / 60), end % 60, 0, 0);
  if (boundary.getTime() <= now) boundary.setDate(boundary.getDate() + 1);
  return boundary.getTime();
}
export function scheduledJobIds(
  schedule: Pick<LearningSchedule, 'jobIds'>,
  jobs: readonly { id: string; status: 'provisional' | 'active' | 'archived' }[],
): string[] {
  // Older multi-job schedules retain one deterministic owner, never a nightly rotation.
  if (schedule.jobIds === 'all')
    return jobs
      .filter((job) => job.status === 'active')
      .map((job) => job.id)
      .slice(0, 1);
  const known = new Set(jobs.filter((job) => job.status !== 'archived').map((job) => job.id));
  return [...new Set(schedule.jobIds.filter((id) => known.has(id)))].slice(0, 1);
}
export const clockLabel = (at: number): string =>
  new Date(at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
